import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { ICol, IRendererConfig, GridEvents, IRow, IEditor } from "../../types";
import { Calendar, CalendarEvents } from "@dhx/ts-calendar";
import { stringToDate } from "@dhx/ts-common/date";
import { Popup, PopupEvents } from "@dhx/ts-popup";

export class DateEditor implements IEditor {
	protected _handlers: { [key: string]: (...args) => void };
	protected _cell: { row: IRow; col: ICol };
	protected _config: IRendererConfig;
	protected _input: HTMLInputElement;
	private _calendar: Calendar;
	private _popup: Popup;
	private _value: string;

	constructor(row: IRow, col: ICol, config: IRendererConfig) {
		this._config = config;
		this._cell = { row, col };
		this._calendar = new Calendar(null, { dateFormat: col.format });
		const value = this._cell.row[this._cell.col.id];
		const format = this._calendar.config.dateFormat;
		if (stringToDate(value, format, true) || value instanceof Date) {
			this._calendar.setValue(value);
			this._value = this._calendar.getValue() as string;
			this._cell.row[this._cell.col.id] = this._value;
		}
		this._popup = new Popup();
		this._popup.attach(this._calendar);
		this._calendar.events.on(CalendarEvents.change, () => {
			this.endEdit(false, true);
		});
		this._popup.events.on(PopupEvents.afterHide, () => {
			this.endEdit();
		});
		this._initHandlers();
	}

	endEdit(withoutSave?: boolean, calendarChange?: boolean) {
		if (!this._handlers) {
			return;
		}
		const format = this._calendar.config.dateFormat;
		const value = this._cell.row[this._cell.col.id];
		if (!withoutSave) {
			if (value instanceof Date || calendarChange) {
				this._value = this._calendar.getValue() as string;
				this._input.value = this._value;
				this._popup.hide();
				return;
			} else if (
				stringToDate(this._input.value, format, true) &&
				((value && this._input.value.length === value.length) || !value)
			) {
				this._value = this._input.value;
			}
		}
		if (
			this._config.events.fire(GridEvents.beforeEditEnd, [this._value, this._cell.row, this._cell.col])
		) {
			this._input.removeEventListener("focus", this._handlers.onFocus);
			this._input.removeEventListener("change", this._handlers.onChange);
			document.removeEventListener("mousedown", this._handlers.onOuterClick);
			awaitRedraw().then(() => {
				this._popup.destructor();
				this._calendar.destructor();
			});
			this._cell.row = this._config.datacollection.getItem(this._cell.row.id);
			this._config.events.fire(GridEvents.afterEditEnd, [this._value, this._cell.row, this._cell.col]);
		} else {
			this._input.focus();
		}
	}

	toHTML() {
		const value = this._cell.row[this._cell.col.id];
		this._config.$editable.editor = this;
		document.addEventListener("mousedown", this._handlers.onOuterClick);
		return el("input.dhx_cell-editor.dhx_cell-editor__input.dhx_cell-editor__datepicker", {
			_hooks: {
				didInsert: this._handlers.didInsert,
			},
			_key: "cell_editor",
			"data-dhx-id": "cell_editor",
			value,
		});
	}

	protected _initHandlers() {
		this._handlers = {
			onFocus: () => {
				awaitRedraw().then(() => {
					this._popup.show(this._input, {
						centering: true,
						mode: "bottom",
					});
				});
			},
			onChange: () => {
				this.endEdit();
			},
			onOuterClick: (e: Event) => {
				if (e.target instanceof Node) {
					const isInput = this._input && this._input.contains(e.target);
					const isPopup =
						this._popup &&
						this._popup.getRootNode() &&
						this._popup.getRootNode().contains(e.target);
					if (!(isInput || isPopup)) {
						this._popup.hide();
					}
				}
			},
			didInsert: (node: any) => {
				const input: HTMLInputElement = node.el;
				this._input = input;
				this._input.addEventListener("focus", this._handlers.onFocus);
				this._input.addEventListener("change", this._handlers.onChange);
				input.focus();
				input.setSelectionRange(input.value.length, input.value.length);
			},
		};
	}
}
